export const BOM_IMPORT_SKU_REQUIRED_REASON = 'sku is required'
export const BOM_IMPORT_SUPPLIER_REQUIRED_REASON = 'supplier is required'

export type BomImportColumnMappingSelection = {
  skuColumn: number
  supplierColumn: number
  nameColumn?: number | null
}

export type BomImportPreviewRow = {
  rowNumber: number
  cells: string[]
}

export type BomImportRowValidationReason =
  | typeof BOM_IMPORT_SKU_REQUIRED_REASON
  | typeof BOM_IMPORT_SUPPLIER_REQUIRED_REASON

export type EvaluatedBomImportRow = {
  rowNumber: number
  sku: string
  supplier: string
  name: string | null
  valid: boolean
  reason: BomImportRowValidationReason | null
  evaluated: boolean
}

function cellAt(cells: string[], columnIndex: number): string {
  if (columnIndex < 1) {
    return ''
  }
  return (cells[columnIndex - 1] ?? '').trim()
}

export type BomImportRowStatusTooltipLabels = {
  statusPending: string
  statusWillImport: string
  statusSkuRequired: string
  statusSupplierRequired: string
  statusSkuAndSupplierRequired: string
}

export function bomImportRowStatusTooltipText(
  row: EvaluatedBomImportRow,
  labels: BomImportRowStatusTooltipLabels
): string {
  if (!row.evaluated) {
    return labels.statusPending
  }
  if (row.valid) {
    return labels.statusWillImport
  }
  if (row.sku === '' && row.supplier === '') {
    return labels.statusSkuAndSupplierRequired
  }
  if (row.supplier === '') {
    return labels.statusSupplierRequired
  }
  return labels.statusSkuRequired
}

export function getBomImportRowFieldValidationFailure(
  sku: string,
  supplier: string
): BomImportRowValidationReason | null {
  if (sku === '') {
    return BOM_IMPORT_SKU_REQUIRED_REASON
  }
  if (supplier === '') {
    return BOM_IMPORT_SUPPLIER_REQUIRED_REASON
  }
  return null
}

export function evaluateBomImportRow(
  row: BomImportPreviewRow,
  mapping: BomImportColumnMappingSelection
): EvaluatedBomImportRow {
  const sku = cellAt(row.cells, mapping.skuColumn)
  const supplier = cellAt(row.cells, mapping.supplierColumn)
  const nameColumn = mapping.nameColumn ?? null
  const name =
    nameColumn == null ? null : cellAt(row.cells, nameColumn) || null
  const reason = getBomImportRowFieldValidationFailure(sku, supplier)

  return {
    rowNumber: row.rowNumber,
    sku,
    supplier,
    name,
    valid: reason == null,
    reason,
    evaluated: true,
  }
}

export function evaluateBomImportRows(
  rows: BomImportPreviewRow[],
  mapping: BomImportColumnMappingSelection | null
): EvaluatedBomImportRow[] {
  if (mapping == null) {
    return rows.map((row) => ({
      rowNumber: row.rowNumber,
      sku: '',
      supplier: '',
      name: null,
      valid: false,
      reason: null,
      evaluated: false,
    }))
  }

  return rows.map((row) => evaluateBomImportRow(row, mapping))
}

export function isBomImportMappingSelectionComplete(input: {
  skuColumn: string
  supplierColumn: string
  nameColumn: string
  nameNoneValue: string
}): BomImportColumnMappingSelection | null {
  if (input.skuColumn === '' || input.supplierColumn === '') {
    return null
  }

  const skuColumn = Number.parseInt(input.skuColumn, 10)
  const supplierColumn = Number.parseInt(input.supplierColumn, 10)
  if (Number.isNaN(skuColumn) || Number.isNaN(supplierColumn)) {
    return null
  }

  if (skuColumn === supplierColumn) {
    return null
  }

  let nameColumn: number | null = null
  if (input.nameColumn !== input.nameNoneValue) {
    nameColumn = Number.parseInt(input.nameColumn, 10)
    if (
      Number.isNaN(nameColumn) ||
      nameColumn === skuColumn ||
      nameColumn === supplierColumn
    ) {
      return null
    }
  }

  return { skuColumn, supplierColumn, nameColumn }
}
